import React from 'react';
import { FieldError } from '@spike/model';
import { useSingleSaleHeaderStyles } from 'components/Payments/SingleSale/Header/SingleSaleHeader.styles';
import { Grid } from '@material-ui/core';
import clsx from 'clsx';
import Title from 'components/Payments/ui/Title';
import { SearchClient } from 'components/Payments/SingleSale/SearchClient/SearchClient';
import ClientInfo from 'components/Payments/SingleSale/ClientInfo/ClientInfo';
import Client from '@spike/client-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import Button from 'components/UI/V3/Button/Button';

interface SingleSaleHeaderProps {
    errors: Array<FieldError>;
    className?: string;
    data: Client;
    date: Moment;
    timeZone: string;
    title?: string;
    onBack?: () => void;
    onAddNewClient: () => void;
    onClientSelected: (clientID: number | undefined) => void;
    onDateChange: (date: Moment) => void;
}

export const SingleSaleHeader: React.FC<SingleSaleHeaderProps> = props => {
    const classes = useSingleSaleHeaderStyles();
    const [isDatePickerExpanded, setIsDatePickerExpanded] = React.useState(false);
    const [showOldDateConfirmation, setShowOldDateConfirmation] = React.useState(false);

    const hasError = React.useMemo(() => props.errors?.some(error => error.fieldName === 'client'), [props.errors]);
    const today = React.useMemo(() => moment().tz(props.timeZone).startOf('day'), [props.timeZone]);

    const changeDateHandler = (date: Moment | null) => {
        const selectedDate = date
            ? moment.tz(date.format('YYYY-MM-DD'), 'YYYY-MM-DD', props.timeZone)
            : moment().tz(props.timeZone);
        const nextDate = selectedDate.isAfter(today) ? today.clone() : selectedDate;

        props.onDateChange(nextDate);
        setIsDatePickerExpanded(false);

        if (nextDate.clone().startOf('day').isBefore(moment().tz(props.timeZone).subtract(90, 'days').startOf('day'))) {
            setShowOldDateConfirmation(true);
        }
    };

    const cancelOldDateHandler = () => {
        props.onDateChange(today.clone());
        setShowOldDateConfirmation(false);
    };

    return (
        <div className={classes.root}>
            <Grid
                container
                className={clsx(classes.container)}
            >
                <Grid
                    item
                    xs={12}
                    md={4}
                    xl={3}
                    className={clsx(classes.containerSearch, { [classes.errorBorder]: hasError }, props.className)}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {props.onBack && (
                            <button
                                type="button"
                                aria-label="Back to client"
                                onClick={props.onBack}
                                style={{ all: 'unset', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </button>
                        )}
                        <Title text={props.title ?? 'New Sale'} />
                    </div>
                    <div className={classes.dateSelector}>
                        <ExpandableSelector
                            id="single-sale-date"
                            title="Date"
                            label={props.date.format('dddd, LL')}
                            expanded={isDatePickerExpanded}
                            childrenClassName={classes.datePickerDropdown}
                            onExpand={() => setIsDatePickerExpanded(prev => !prev)}
                            onClickAway={() => setIsDatePickerExpanded(false)}
                        >
                            <DatePicker
                                timeZone={props.timeZone}
                                value={props.date}
                                maxDate={today}
                                onChange={changeDateHandler}
                            />
                        </ExpandableSelector>
                    </div>
                    <div className={classes.boxSearch}>
                        <SearchClient
                            parentName={props.data ? `${props.data.firstName} ${props.data.lastName}` : undefined}
                            onAddClient={props.onAddNewClient}
                            onSelectClient={props.onClientSelected}
                        />
                    </div>
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={5}
                >
                    {props.data && <ClientInfo data={props.data} />}
                </Grid>
            </Grid>
            {showOldDateConfirmation && (
                <GenericModal
                    onClose={() => setShowOldDateConfirmation(false)}
                    footer={
                        <div className={classes.confirmationFooter}>
                            <Button
                                fullWidth
                                label="No"
                                variant="black"
                                onClick={cancelOldDateHandler}
                            />
                            <Button
                                fullWidth
                                label="Yes"
                                variant="green"
                                onClick={() => setShowOldDateConfirmation(false)}
                            />
                        </div>
                    }
                >
                    <h2 className={classes.confirmationTitle}>Confirm Date</h2>
                    <p className={classes.confirmationQuestion}>This date is more than 90 days ago. Are you sure?</p>
                </GenericModal>
            )}
        </div>
    );
};
